import * as React from 'react';

import type { ColumnFiltersState } from '@tanstack/react-table';
import type { ColumnFilterShowDropdownState } from 'src/registry/components/table/types';

type FilterObject = {
    value: {
        beforeDate: Date;
        afterDate: Date;
        beforeTime: string;
        afterTime: string;
    };
    id: string;
};

interface useFiltersProps {
    initialQueryFilters?: Record<string, string>;
}

function useFilters({ initialQueryFilters = {} }: useFiltersProps = {}) {
    // This is in order to enable the custom logic to have dropdowns for filter values/options
    // without specifying any filter value yet.

    const initialFiltersArray: ColumnFiltersState = React.useMemo(() => {
        return Object.entries(initialQueryFilters).map(([key, value]) => {
            return {
                id: key,
                value: value?.includes(',') ? value.split(',') : value,
            };
        });
    }, [initialQueryFilters]);

    const [columnFiltersShowDropdownState, setColumnFiltersShowDropdownState] =
        React.useState<ColumnFilterShowDropdownState>(() => initialFiltersArray.map((f) => f.id));
    const [columnFiltersState, setColumnFiltersState] = React.useState<ColumnFiltersState>(initialFiltersArray);

    const queryFiltersOptions = React.useMemo(() => {
        return columnFiltersState.reduce((acc, filter) => {
            // If we don't show the dropdown, we don't want to include the filter in the query
            if (!columnFiltersShowDropdownState.includes(filter.id)) return acc;
            if (typeof filter.value === 'object') {
                const {
                    value: { beforeDate, afterDate },
                    id,
                } = filter as FilterObject;

                if (id === 'inline_date_filters') {
                    const from = afterDate ? afterDate.toISOString() : undefined;
                    const to = beforeDate ? beforeDate.toISOString() : undefined;

                    return {
                        ...acc,
                        ...(from ? { from } : {}),
                        ...(to ? { to } : {}),
                    };
                }

                if (Object.hasOwn(filter.value as object, 'beforeDate')) {
                    const {
                        value: { beforeDate, afterDate },
                        id,
                    } = filter as FilterObject;
                    const afterProp = `${[id]}_after`;
                    const beforeProp = `${[id]}_before`;

                    const idsWithYYYYMMDDFormat = [
                        'exit_time',
                        'entry_time',
                        'valid_from',
                        'valid_to',
                        'billing_period_start',
                        'billing_period_end',
                        'business_date',
                    ];

                    const afterValue = idsWithYYYYMMDDFormat.includes(id)
                        ? afterDate.toISOString().split('T')[0]
                        : afterDate.toISOString();
                    const beforeValue = idsWithYYYYMMDDFormat.includes(id)
                        ? beforeDate.toISOString().split('T')[0]
                        : beforeDate.toISOString();

                    return { ...acc, [afterProp]: afterValue, [beforeProp]: beforeValue };
                }
            }

            if (filter.id === 'date_greater_than') {
                const targetDate = filter.value as Date;
                const date_greater_than = targetDate ? targetDate.toISOString() : undefined;

                return {
                    ...acc,
                    ...(date_greater_than ? { date_greater_than } : {}),
                };
            }
            if (filter.id === 'date_less_than') {
                const targetDate = filter.value as Date;
                const date_less_than = targetDate ? targetDate.toISOString() : undefined;

                return {
                    ...acc,
                    ...(date_less_than ? { date_less_than } : {}),
                };
            }
            return { ...acc, [filter.id]: filter.value };
        }, {});
    }, [columnFiltersState, columnFiltersShowDropdownState]);

    return {
        columnFiltersState,
        setColumnFiltersState,
        columnFiltersShowDropdownState,
        setColumnFiltersShowDropdownState,
        queryFiltersOptions,
    };
}

export default useFilters;
